import { CheckCircleOutline, LinkRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router";
import { v4 as newGuid } from "uuid";
import {
  Autocomplete,
  SmallInputLabel,
  SnackbarContent,
  SurveyResponseAnswerView,
  TextEditor,
  UserAutocomplete,
} from "..";
import { Links } from "../../constants";
import { useSession } from "../../hooks";
import { useBulkCreateTaskItems, useCreateTaskItem, useGetTeams, useGetUsers } from "../../http";
import { ISurveyResponseAnswer, ITeamSummary, IUser } from "../../models";

interface UrlParams {
  slug: string;
}

interface ICreateTaskFormState {
  description: string;
  notes: string;
  owners: IUser[];
  primaryOwner: IUser | null;
  splitPerOwner: boolean;
  team: ITeamSummary | null;
  parentTaskItemGuid?: string;
}

interface IProps {
  visible: boolean;
  context?: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  parentTask?: {
    taskItemGuid: string;
    description: string;
  };
  createdFromSurveyResponseAnswer?: ISurveyResponseAnswer;
  defaultSelectedTeamSlug?: string;
  onCreated: () => void;
  onCancelled: () => void;
}

const CreateTaskItemModal = (props: IProps) => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const location = useLocation();
  const { userId: sessionUserGuid } = useSession();
  const { isLoading: isLoadingTeams, data: teams } = useGetTeams(sessionUserGuid, props.visible);
  const { isLoading: isLoadingUsers, data: users } = useGetUsers(props.visible);
  const { mutate: createTask, isLoading: isCreatingTask } = useCreateTaskItem();
  const { mutate: bulkCreateTasks, isLoading: isBulkCreatingTasks } = useBulkCreateTaskItems();
  const { enqueueSnackbar } = useSnackbar();

  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<ICreateTaskFormState>({
    defaultValues: {
      description: "",
      notes: "",
      team: null,
      owners: [],
      primaryOwner: null,
      splitPerOwner: false,
    },
  });

  const owners = watch("owners");
  const splitPerOwner = watch("splitPerOwner");
  const primaryOwner = watch("primaryOwner");

  useEffect(() => {
    if (users && props.visible) {
      setValue(
        "owners",
        users.filter((x) => x.userId === sessionUserGuid)
      );
    }
  }, [setValue, sessionUserGuid, users, props.visible]);

  useEffect(() => {
    if (teams && props.visible) {
      const team = teams.find((x) => x.slug === (props.defaultSelectedTeamSlug ?? slug));

      if (team?.isAdministrator || team?.isMember) {
        setValue("team", team);
      } else {
        setValue("team", null);
      }
    }
  }, [setValue, slug, teams, props.visible, props.defaultSelectedTeamSlug]);

  function renderSnackbarOwnerList(owners: IUser[], limit: number) {
    const displayedOwners = owners
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .slice(0, limit)
      .map((owner, index) => (
        <Box key={index} component="span">
          {index > 0 ? ", " : ""}
          <Link onClick={(e) => e.stopPropagation()} href={Links.UserImplementation(owner.userId)}>
            {owner.displayName}
            {owner.enabled ? "" : " (deactivated)"}
          </Link>
        </Box>
      ));

    const remainingCount = owners.length - limit;

    return (
      <>
        {displayedOwners}
        {remainingCount > 0 && `, +${remainingCount} more.`}
      </>
    );
  }

  async function handleAddClicked() {
    if (splitPerOwner && owners.length > 1) {
      handleSubmit((formState) => {
        bulkCreateTasks(
          {
            taskItems: formState.owners.map((owner) => ({
              taskItemGuid: newGuid(),
              teamSlug: formState.team?.slug,
              parentTaskItemGuid: props.parentTask?.taskItemGuid,
              contextActionItemGuid: props.context?.actionItemGuid,
              description: formState.description,
              dueDate: null,
              note: formState.notes,
              owners: [owner],
              primaryOwner: null,
              createdFromSurveyResponseAnswerGuid: props.createdFromSurveyResponseAnswer?.answerGuid,
            })),
          },
          {
            onSuccess: () => {
              reset(undefined, { keepDefaultValues: true });
              enqueueSnackbar(<SnackbarContent title={`${owners.length} Tasks Created`} />, {
                variant: "success",
              });

              props.onCreated();
            },
          }
        );
      })();
    } else {
      const newTaskGuid = newGuid();

      handleSubmit((formState) => {
        createTask(
          {
            taskItemGuid: newTaskGuid,
            teamSlug: formState.team?.slug,
            parentTaskItemGuid: props.parentTask?.taskItemGuid,
            contextActionItemGuid: props.context?.actionItemGuid,
            description: formState.description,
            dueDate: null,
            note: formState.notes,
            owners: formState.owners,
            primaryOwner: formState.primaryOwner,
            createdFromSurveyResponseAnswerGuid: props.createdFromSurveyResponseAnswer?.answerGuid,
          },
          {
            onSuccess: () => {
              reset(undefined, { keepDefaultValues: true });

              if (
                formState.team
                  ? Links.TeamImplementation(formState.team.slug) !== location.pathname
                  : !formState.owners.some((owner) => Links.UserImplementation(owner.userId) === location.pathname)
              ) {
                enqueueSnackbar(
                  <SnackbarContent
                    title="Task Created"
                    content={
                      formState.team != null ? (
                        <>
                          Task added to{" "}
                          <Link href={Links.TeamImplementation(formState.team.slug)}>{formState.team.name}</Link>
                        </>
                      ) : (
                        <>Task assigned to {renderSnackbarOwnerList(formState.owners, 2)}</>
                      )
                    }
                  />,
                  {
                    variant: "success",
                    action: <Button href={Links.TaskItemDetail(newTaskGuid)}>View Task</Button>,
                  }
                );
              }

              props.onCreated();
            },
          }
        );
      })();
    }
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Add Task</DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        <Stack spacing={1}>
          {props.context && (
            <Stack sx={{ px: 1 }}>
              <SmallInputLabel>Context</SmallInputLabel>
              <Stack direction="row" spacing={0.25} sx={{ alignItems: "start" }}>
                <LinkRounded sx={{ position: "relative", top: 4 }} />
                <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  <Box component="span" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    {props.context?.teamName}
                  </Box>
                  {` : ${props.context?.actionItemDescription}`}
                </Typography>
              </Stack>
            </Stack>
          )}

          {props.parentTask && (
            <Stack sx={{ px: 1 }}>
              <SmallInputLabel>Parent Task</SmallInputLabel>
              <Stack direction="row" spacing={0.25} sx={{ alignItems: "start" }}>
                <CheckCircleOutline sx={{ position: "relative", top: 4 }} />
                <Typography>
                  <Box component="span" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    {props.parentTask.description}
                  </Box>
                </Typography>
              </Stack>
            </Stack>
          )}

          <Stack spacing={0.25} sx={{ px: 1 }}>
            <SmallInputLabel>Description *</SmallInputLabel>
            <Controller
              name="description"
              control={control}
              rules={{ required: { message: "Description is required", value: true } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors["description"] !== undefined}
                  helperText={errors["description"]?.message}
                  placeholder="Enter item..."
                  autoFocus
                  multiline
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25} sx={{ px: 1 }}>
            <SmallInputLabel>Notes</SmallInputLabel>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => <TextEditor initialValue={field.value} onChange={field.onChange} />}
            />
          </Stack>
          <Stack spacing={0.25} sx={{ px: 1 }}>
            <SmallInputLabel>Team To Process</SmallInputLabel>
            <Controller
              name="team"
              control={control}
              rules={{
                validate: (_, formValues) => {
                  return (
                    formValues.team != null ||
                    formValues.owners.length > 0 ||
                    "At least one owner or assigned team is required"
                  );
                },
              }}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isLoadingTeams}
                  disabled={isLoadingTeams}
                  options={teams ?? []}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <MenuItem
                      component="li"
                      disabled={!option.isAdministrator && !option.isMember}
                      {...props}
                      key={option.slug}
                    >
                      <Stack>
                        <Typography>{option.name}</Typography>

                        {!(option.isAdministrator || option.isMember) && (
                          <Typography variant="caption">You are not a member or administrator of this team</Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  )}
                  onChange={(_, option) => field.onChange(option)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select a team..."
                      inputProps={{ ...params.inputProps }}
                      error={errors["team"] !== undefined}
                      helperText={errors["team"]?.message}
                    />
                  )}
                  value={field.value}
                  isOptionEqualToValue={(option, value) => option.slug === value.slug}
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25} sx={{ px: 1 }}>
            <SmallInputLabel>Ownership</SmallInputLabel>
            <Controller
              name="owners"
              control={control}
              rules={{
                validate: (_, formValues) => {
                  return (
                    formValues.team != null ||
                    formValues.owners.length > 0 ||
                    "At least one owner or assigned team is required"
                  );
                },
              }}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isLoadingUsers}
                  disabled={isLoadingUsers}
                  options={users ?? []}
                  getOptionLabel={(option) => option.displayName}
                  isOptionEqualToValue={(option, value) => option.userId === value.userId}
                  renderOption={(props, option) => (
                    <MenuItem component="li" {...props} key={option.userId}>
                      <Stack>
                        <Typography>{option.displayName}</Typography>
                        <Typography variant="caption">{option.email}</Typography>
                      </Stack>
                    </MenuItem>
                  )}
                  onChange={(_, value) => {
                    if (value.length <= 1) {
                      setValue("primaryOwner", null);
                      setValue("splitPerOwner", false);
                    }

                    if (value.some((owner) => owner.userId === primaryOwner?.userId) === false) {
                      setValue("primaryOwner", null);
                    }
                    field.onChange(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select people..."
                      inputProps={{ ...params.inputProps }}
                      error={errors["team"] !== undefined}
                      helperText={errors["team"]?.message}
                    />
                  )}
                  value={field.value}
                  multiple
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25} sx={{ px: 1 }}>
            <SmallInputLabel disabled={owners.length <= 1 || splitPerOwner}>Primary Owner</SmallInputLabel>
            <Controller
              name="primaryOwner"
              control={control}
              render={({ field }) => (
                <UserAutocomplete
                  options={owners}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select a primary owner..."
                  disabled={owners.length <= 1 || splitPerOwner}
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25} sx={{ px: 1, width: "100%" }}>
            <SmallInputLabel disabled={owners.length <= 1}>Split Per Owner</SmallInputLabel>
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Typography color={owners.length <= 1 ? "text.disabled" : "inherit"}>
                Create separate tasks, one per owner
              </Typography>
              <Controller
                name="splitPerOwner"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={(e) => {
                      setValue("primaryOwner", null);
                      field.onChange(e.target.checked);
                    }}
                    sx={{ m: 0 }}
                    disabled={owners.length <= 1}
                  />
                )}
              />
            </Stack>
          </Stack>

          {props.createdFromSurveyResponseAnswer && (
            <Stack spacing={1} sx={{ px: 1 }}>
              <Typography>Created task will reference the survey response: </Typography>
              <SurveyResponseAnswerView surveyResponseAnswer={props.createdFromSurveyResponseAnswer} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isCreatingTask} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isCreatingTask || isBulkCreatingTasks}
          endIcon={
            isCreatingTask || isBulkCreatingTasks ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null
          }
          onClick={handleAddClicked}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateTaskItemModal };
